import _ from 'lodash'
import { useCallback, useEffect, useRef, useState } from 'react'
import { evaluateMappings } from 'shared-libs/models/types/storyboard/storyboard-utils'
import { EntitySearchSelectionPayload, UI2EntitySearchFieldProps } from './ui2-entity-search-field-props'
import type { EditableFieldInternal } from '../core-field/editable-field-internal'

interface SelectionPayload {
  entity: any
  searchFilter: string
}

interface UseEntitySearchSelectionArgs {
  value: UI2EntitySearchFieldProps['value']
  valueMappings: UI2EntitySearchFieldProps['valueMappings']
  frames: any
  onChange: EditableFieldInternal['onChange']
  onEntityChange: UI2EntitySearchFieldProps['onEntityChange']
  rawQueryPath?: string
  /** Platform-specific fn to fetch an entity by uniqueId from the store. */
  fetchEntity: (uniqueId: string) => Promise<any>
}

interface UseEntitySearchSelectionResult {
  entity: any
  isLoading: boolean
  /** Increments each time selection changes (commit/clear) — use as a React key to re-trigger animations. */
  pulseTick: number
  handleCommitSelection: (payload: SelectionPayload) => void
  handleClear: () => void
}

/**
 * Manages the selection lifecycle for the entity search field:
 * hydrates the current value on mount/change, writes back on commit,
 * clears, and fires `onEntityChange`.
 */
export function useEntitySearchSelection({
  value,
  valueMappings = [],
  frames,
  onChange,
  onEntityChange,
  rawQueryPath,
  fetchEntity,
}: UseEntitySearchSelectionArgs): UseEntitySearchSelectionResult {
  const [entity, setEntity] = useState<any>(undefined)
  const [isLoading, setIsLoading] = useState(false)
  const [pulseTick, setPulseTick] = useState(0)

  const lastEntityId = useRef<string | undefined>(undefined)

  useEffect(() => {
    const entityId = _.get(value, 'entityId')
    if (entityId === lastEntityId.current) return
    lastEntityId.current = entityId

    if (!entityId) {
      setEntity(undefined)
      return
    }

    let cancelled = false
    setIsLoading(true)
    fetchEntity(entityId).then((fetched) => {
      if (cancelled) return
      setEntity(fetched)
      setIsLoading(false)
    }).catch(() => {
      if (!cancelled) setIsLoading(false)
    })

    return () => { cancelled = true }
  }, [value, fetchEntity])

  const handleCommitSelection = useCallback(
    ({ entity: selected, searchFilter }: SelectionPayload) => {
      setEntity(selected)
      setPulseTick((t) => t + 1)

      const mappedData =
        evaluateMappings(valueMappings, frames, {
          item: selected,
          entity: selected,
          searchFilter,
          isCreateEntitySelected: false,
          ...selected,
        }) ?? {}

      onChange?.({ entityId: selected?.uniqueId, displayName: selected?.displayName, ...mappedData })
      onEntityChange?.({ entityId: selected?.uniqueId, entity: selected } as EntitySearchSelectionPayload)

      if (rawQueryPath) {
        // Write the entity displayName to the raw path so submit-mode inputs
        // reflect the committed selection as plain text.
        ;(onChange as any)?.(selected?.displayName || searchFilter, false, rawQueryPath)
      }
    },
    [valueMappings, frames, onChange, onEntityChange, rawQueryPath],
  )

  const handleClear = useCallback(() => {
    setEntity(undefined)
    setPulseTick((t) => t + 1)
    onChange?.({})
    onEntityChange?.({ entityId: undefined, entity: undefined })
    if (rawQueryPath) {
      ;(onChange as any)?.(undefined, false, rawQueryPath)
    }
  }, [onChange, onEntityChange, rawQueryPath])

  return { entity, isLoading, pulseTick, handleCommitSelection, handleClear }
}
